import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './NewPerformance.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fireAudit, buildDiffPreview } from '../utils/auditClient';

const NewPerformance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('auth-token');
  const [formData, setFormData] = useState({
    playerName: '',
    batch: '',
    month: '',
    year: '',
    scores: []
  });
  const baselineRef = useRef(null);
  const { batchId, playerId, selectedMonth: month, selectedYear: year } = location.state || {};
  const isEdit = !!(month && year);

  const buildPayloadFromForm = (fd) => ({
    player: { id: fd.playerId },
    batch: { id: parseInt(fd.batch) },
    month: fd.month,
    year: fd.year,
    progressScoreForms: (fd.scores || []).map(s => ({
      id: s.id || null,
      progressParameter: { id: s.parameterId },
      score: parseInt(s.score)
    })),
  });

  const fetchData = async () => {
    try {
      if (isEdit) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_PERFORMANCE_POST}/${batchId}/player/${playerId}/attributes?month=${month}&year=${year}`,
          {
            headers: {
              "Content-Type": "application/json",
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        const data = response.data.data.performanceForm;
        const updatedFormData = {
          playerId: data.player.id,
          playerName: data.player.user.name,
          batch: batchId.toString(),
          batchName: data?.batch?.name,
          month: data.month,
          year: data.year,
          scores: data.progressScoreForms.map((item) => ({
            id: item.id,
            attribute: item.progressParameter?.name,
            parameter: item.progressParameter?.name,
            attributeId: item.progressParameter?.id,
            parameterId: item.progressParameter?.id,
            score: item.score,
          })),
        };
        setFormData(updatedFormData);
        baselineRef.current = buildPayloadFromForm(updatedFormData);
      } else {
        const response = await axios.get(
          `${process.env.REACT_APP_API_PERFORMANCE_POST}/${batchId}/player/${playerId}/new`,
          {
            headers: {
              "Content-Type": "application/json",
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        const data = response.data.data.performanceForm;
        const updatedFormData = {
          playerId: data.player.id,
          batchName: data?.batch?.name,
          playerName: data?.player?.user?.name,
          batch: batchId.toString(),
          month: data.month,
          year: data.year,
          scores: data.progressScoreForms.map((item) => ({
            attribute: item.progressParameter?.name,
            parameter: item.progressParameter?.name,
            attributeId: item.progressParameter?.id,
            parameterId: item.progressParameter?.id,
            score: item.score,
          })),
        };
        setFormData(updatedFormData);
        baselineRef.current = buildPayloadFromForm(updatedFormData);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load performance data",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (index, value) => {
    const updatedScores = [...formData.scores];
    updatedScores[index].score = value;
    setFormData({ ...formData, scores: updatedScores });
  };

  const handleSubmit = async () => {
    const performanceData = buildPayloadFromForm(formData);
    const isUpdate = formData.scores.some(score => score.id);

    if (isUpdate) {
      const { digest, html } = buildDiffPreview(baselineRef.current || {}, performanceData);
      if (!digest.length) {
        Swal.fire({ icon: 'info', title: 'Nothing to update', text: 'No differences detected.', toast: true, position: 'top-end', showConfirmButton: false, timer: 2500 });
        return;
      }
      const confirm = await Swal.fire({ title: 'Apply these changes?', html, showCancelButton: true, confirmButtonText: 'Update' });
      if (!confirm.isConfirmed) return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_PERFORMANCE_POST}/${batchId}/player/${playerId}/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(performanceData),
      });

      const data = await response.json();

      if (response.ok) {
        const action = isUpdate ? 'update' : 'create';
        const affected_id = data?.data?.id || `${batchId}-${playerId}-${formData.month}-${formData.year}`;
        fireAudit({
          entity: 'performance',
          affected_id,
          affected_user: playerId,
          action,
          section: 'Coaching',
          subSection: 'New Performance',
          businessApi: `${process.env.REACT_APP_API_PERFORMANCE_POST}/${batchId}/player/${playerId}/new`,
          businessMethod: 'POST',
          oldPayload: baselineRef.current || {},
          newPayload: performanceData,
          extra: { page: 'NewPerformance' },
        });
        baselineRef.current = performanceData;

        navigate(-1);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: isUpdate
            ? 'Performance data updated successfully'
            : 'Performance data submitted successfully',
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to submit performance data",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
    }
  };

  return (
    <div className="enrollment-container">
      <div className="performance-form">
        <div className="performance-info">
          <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
            <input
              type="text"
              id="playerName"
              name="playerName"
              className="walkin-form has-value"
              value={formData.playerName}
              disabled
            />
            <label htmlFor="playerName">Player Name</label>
          </div>

          <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
            <input
              type="text"
              id="batchName"
              name="batchName"
              className="walkin-form has-value"
              value={formData.batchName}
              disabled
            />
            <label htmlFor="batchName">Batch</label>
          </div>

          <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
            <input
              type="text"
              id="month"
              name="month"
              className="walkin-form has-value"
              value={formData.month}
              disabled
            />
            <label htmlFor="month">Month</label>
          </div>

          <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
            <input
              type="text"
              id="year"
              name="year"
              className="walkin-form has-value"
              value={formData.year}
              disabled
            />
            <label htmlFor="year">Year</label>
          </div>

        </div>

        <div className="rental-table-scroll">
          <table className="rental-entry-table">
            <thead>
              <tr>
                <th>Attribute</th>
                <th>Parameter</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {formData.scores.map((score, index) => (
                <tr key={index}>
                  <td>{score.attribute}</td>
                  <td>{score.parameter}</td>
                  <td>
                    <input
                      className="rental-input"
                      type="number"
                      step="0.01"
                      value={score.score}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      placeholder='0'
                      onWheel={e => e.target.blur()}
                      onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="submit-button" onClick={handleSubmit}>Submit</button>
      </div>
    </div>
  );
};

export default NewPerformance;
